import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import useSettings from '../hooks/useSettings';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus, FlaskConical, X, Eye, ChevronLeft, ChevronRight, Image as ImageIcon, Sliders, ToggleLeft, ToggleRight, PackagePlus } from 'lucide-react';
import {
  getProducts, createProduct, updateProduct, deleteProduct, toggleProductActive, getProductCostHistory,
  uploadProductImage, deleteProductImage, reorderProductImages,
  getProductStructure, saveProductStructure, getProductVariants,
} from '../api/products';
import { getRawMaterials, createRawMaterial, updateRawMaterial, deleteRawMaterial, getProductBom, replaceProductBom, getRawMaterialPurchases } from '../api/rawMaterials';
import { getCategories } from '../api/categories';
import { getSourcers } from '../api/sourcers';
import { receiveSourcingOrderDirect } from '../api/sourcingOrders';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import FilterBar from '../components/FilterBar';
import { SourcingOrderForm, EMPTY_FORM as EMPTY_STOCK_FORM, buildSourcingPayload } from '../components/SourcingOrderForm';
import FieldDefinitionEditor, { cleanFields } from '../components/FieldDefinitionEditor';
import MarkdownEditor from '../components/MarkdownEditor';
import { useAuth } from '../context/AuthContext';
import { canWrite } from '../utils/permissions';
import { useTranslation } from 'react-i18next';
import SourcingOrders from './SourcingOrders';

const EMPTY_PRODUCT_FORM = { title: '', description: '', formattedDesc: '', price: '', mrp: '', brand: '', categoryId: '', subcategoryId: '' };
const EMPTY_RM_FORM = { name: '', unit: 'pcs', categoryId: '', description: '' };
const UNITS = ['kg', 'g', 'liter', 'ml', 'pcs'];
const MAX_IMAGES = 5;
const IMAGE_SIZE_WARNING_BYTES = 500 * 1024; // 500 KB
const MAX_VARIANT_CHIPS = 4; // per product row; the rest via "+N more" → Stock by Variant popup

// Row chips skip 0-stock variants; the Stock by Variant popup still lists every variant.
const inStockVariants = (p) => (p.variants || []).filter(v => v.stockQuantity > 0);

function formatPrice(value) {
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatCombo(combo) {
  if (!combo || Object.keys(combo).length === 0) return '—';
  return Object.entries(combo).map(([k, v]) => `${k}: ${v}`).join(', ');
}

function stockBadgeClass(qty) {
  return qty === 0 ? 'bg-red-50 text-red-600'
    : qty <= 5 ? 'bg-yellow-50 text-yellow-700'
    : 'bg-green-50 text-green-700';
}

function Tip({ label, children }) {
  return (
    <span className="relative group inline-flex">
      {children}
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-xs text-white bg-gray-800 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-20">
        {label}
      </span>
    </span>
  );
}

function StatusBadge({ active }) {
  const { t } = useTranslation();
  return active ? (
    <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700">{t('common.active')}</span>
  ) : (
    <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{t('common.inactive')}</span>
  );
}

function RmStockBadge({ qty }) {
  const { t } = useTranslation();
  const n = parseFloat(qty);
  if (n <= 0) return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600">{t('rawMaterials.outOfStock')}</span>;
  if (n < 5)  return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700">{t('rawMaterials.low')}</span>;
  return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700">{t('rawMaterials.inStock')}</span>;
}

// Image item in the modal: { kind: 'existing', id, url } | { kind: 'new', file, previewUrl }
function buildImageList(existingImages) {
  return (existingImages || []).map(img => ({ kind: 'existing', id: img.id, url: img.url }));
}

export default function Inventory() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { user } = useAuth();
  const write = canWrite(user?.role);
  const { simpleView } = useSettings();

  // Tabs (?tab=…): raw materials are hidden in Simple view; sourcing history is always available.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = [
    { key: 'products', label: t('inventory.tabProducts') },
    ...(simpleView ? [] : [{ key: 'raw-materials', label: t('inventory.tabRawMaterials') }]),
    { key: 'history', label: t('inventory.tabSourcingHistory') },
  ];
  const requestedTab = searchParams.get('tab');
  const tab = tabs.some((x) => x.key === requestedTab) ? requestedTab : 'products';
  const fileInputRef = useRef(null);

  usePageTitle('Inventory');

  // ── Products state ─────────────────────────────────────────────
  const [productPage, setProductPage] = useState(0);
  const [productFilters, setProductFilters] = useState({});
  const [productModal, setProductModal] = useState(null); // null | { mode: 'create' | 'edit', data? }
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT_FORM);
  const [productFields, setProductFields] = useState([]); // this product's own field-structure, edited inline
  const [productFormError, setProductFormError] = useState('');
  const [productDeleteTarget, setProductDeleteTarget] = useState(null);
  const [bomModal, setBomModal] = useState(null);
  const [bomItems, setBomItems] = useState([]);
  const [bomError, setBomError] = useState('');
  const [costHistoryModal, setCostHistoryModal] = useState(null);
  const [variantDetailModal, setVariantDetailModal] = useState(null); // { product }

  // ── Common (global/vendor-wide) Structure state ──────────────────
  const [globalStructureModal, setGlobalStructureModal] = useState(false);
  const [globalStructureFields, setGlobalStructureFields] = useState([]);
  const [globalStructureError, setGlobalStructureError] = useState('');

  // ── Image state ────────────────────────────────────────────────
  const [imageList, setImageList] = useState([]); // array of { kind, id?, url?, file?, previewUrl? }
  const [deletedImageIds, setDeletedImageIds] = useState([]);

  // ── Raw Materials state ────────────────────────────────────────
  const [rmPage, setRmPage] = useState(0);
  const [rmModal, setRmModal] = useState(null);
  const [rmForm, setRmForm] = useState(EMPTY_RM_FORM);
  const [rmFormError, setRmFormError] = useState('');
  const [rmDeleteTarget, setRmDeleteTarget] = useState(null);
  const [batchesModal, setBatchesModal] = useState(null);

  // ── Add Stock (direct-received sourcing order) state ──────────
  const [addStockModal, setAddStockModal] = useState(null); // null | { initialForm }
  const [addStockError, setAddStockError] = useState('');

  // ── Shared: categories ─────────────────────────────────────────
  const { data: categoriesData } = useQuery({
    queryKey: ['categories-all'],
    queryFn: () => getCategories(0, 100).then((r) => r.data.data.content),
  });
  const categories = categoriesData ?? [];
  const subcategoriesOf = (categoryId) =>
    categories.find((c) => String(c.id) === String(categoryId))?.subcategories ?? [];
  const formSubcategories = subcategoriesOf(productForm.categoryId);

  // ── Product Structure queries ──────────────────────────────────
  // Vendor-wide (global/"Common") structure — fields shared by every product
  const { data: globalStructureData, refetch: refetchGlobalStructure } = useQuery({
    queryKey: ['product-structure'],
    queryFn: () => getProductStructure().then(r => r.data.data),
  });

  const globalStructureSaveMutation = useMutation({
    mutationFn: (fields) => saveProductStructure(fields),
    onSuccess: () => {
      refetchGlobalStructure();
      queryClient.invalidateQueries({ queryKey: ['product-structure'] });
      setGlobalStructureModal(false);
      setGlobalStructureError('');
    },
    onError: (err) => setGlobalStructureError(err.response?.data?.message || 'Failed to save structure'),
  });

  const openGlobalStructureModal = () => {
    setGlobalStructureModal(true);
    setGlobalStructureError('');
  };

  const handleGlobalStructureSave = () => {
    globalStructureSaveMutation.mutate(cleanFields(globalStructureFields));
  };

  useEffect(() => {
    if (globalStructureData !== undefined) {
      setGlobalStructureFields(globalStructureData?.fields ?? []);
    }
  }, [globalStructureData]);

  // ── This product's own field-structure seeding ───────────────────
  useEffect(() => {
    if (!productModal) return;
    setProductFields(productModal.mode === 'edit' ? (productModal.data.fields ?? []) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productModal?.mode, productModal?.data?.id]);

  // ── Products queries ───────────────────────────────────────────
  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['products', productPage, productFilters],
    queryFn: () => getProducts(productPage, 10, productFilters).then((r) => r.data.data),
  });

  const { data: rawMaterialsAllData } = useQuery({
    queryKey: ['raw-materials-all'],
    queryFn: () => getRawMaterials(0, 200).then(r => r.data.data),
  });
  const rawMaterialsList = rawMaterialsAllData?.content ?? [];

  const { data: bomData } = useQuery({
    queryKey: ['product-bom', bomModal?.product?.id],
    queryFn: () => getProductBom(bomModal.product.id).then(r => r.data.data),
    enabled: !!bomModal,
  });

  useEffect(() => {
    if (bomData) {
      setBomItems(bomData.items.map(i => ({ rawMaterialId: String(i.rawMaterialId), quantity: String(i.quantity) })));
    }
  }, [bomData]);

  const { data: costHistory, isLoading: costHistoryLoading } = useQuery({
    queryKey: ['product-cost-history', costHistoryModal?.product?.id],
    queryFn: () => getProductCostHistory(costHistoryModal.product.id).then(r => r.data.data),
    enabled: !!costHistoryModal,
  });

  const { data: variantDetailData, isLoading: variantDetailLoading } = useQuery({
    queryKey: ['product-variants', variantDetailModal?.product?.id],
    queryFn: () => getProductVariants(variantDetailModal.product.id).then(r => r.data.data),
    enabled: !!variantDetailModal,
  });

  // ── Raw Materials queries ──────────────────────────────────────
  const { data: rmData, isLoading: rmLoading } = useQuery({
    queryKey: ['raw-materials', rmPage],
    queryFn: () => getRawMaterials(rmPage, 20).then(r => r.data.data),
  });

  const { data: batchesData, isLoading: batchesLoading } = useQuery({
    queryKey: ['rm-batches', batchesModal?.rm?.id],
    queryFn: () => getRawMaterialPurchases(batchesModal.rm.id, 0, 100).then(r => r.data.data),
    enabled: !!batchesModal,
  });

  // ── Products mutations ─────────────────────────────────────────
  const invalidateProducts = () => queryClient.invalidateQueries({ queryKey: ['products'] });

  const productSaveMutation = useMutation({
    mutationFn: async (payload) => {
      let productId;
      let savedData;

      if (productModal?.mode === 'edit') {
        const res = await updateProduct(productModal.data.id, payload);
        savedData = res.data.data;
        productId = savedData.id;

        for (const imgId of deletedImageIds) {
          await deleteProductImage(productId, imgId);
        }
      } else {
        const res = await createProduct(payload);
        savedData = res.data.data;
        productId = savedData.id;
      }

      // Upload new images and collect resulting IDs keyed by original index
      const uploadedIds = {};
      for (let i = 0; i < imageList.length; i++) {
        const item = imageList[i];
        if (item.kind === 'new') {
          const imgRes = await uploadProductImage(productId, item.file);
          uploadedIds[i] = imgRes.data.data.id;
        }
      }

      // Build final ordered ID list
      const orderedIds = [];
      for (let i = 0; i < imageList.length; i++) {
        const item = imageList[i];
        if (item.kind === 'existing' && !deletedImageIds.includes(item.id)) {
          orderedIds.push(item.id);
        } else if (item.kind === 'new' && uploadedIds[i] != null) {
          orderedIds.push(uploadedIds[i]);
        }
      }

      if (orderedIds.length > 1) {
        await reorderProductImages(productId, orderedIds);
      }

      return savedData;
    },
    onSuccess: () => {
      invalidateProducts();
      setProductModal(null);
    },
    onError: (err) => setProductFormError(err.response?.data?.message || 'Something went wrong.'),
  });

  const productDeleteMutation = useMutation({
    mutationFn: (id) => deleteProduct(id),
    onSuccess: () => { invalidateProducts(); setProductDeleteTarget(null); },
  });

  const productToggleMutation = useMutation({
    mutationFn: (id) => toggleProductActive(id),
    onSuccess: () => invalidateProducts(),
  });

  const bomMutation = useMutation({
    mutationFn: (items) => replaceProductBom(bomModal.product.id, items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-bom', bomModal.product.id] });
      queryClient.invalidateQueries({ queryKey: ['raw-materials'] });
      setBomModal(null);
      setBomError('');
    },
    onError: (err) => setBomError(err.response?.data?.message || 'Failed to save BOM'),
  });

  // ── Raw Materials mutations ────────────────────────────────────
  const invalidateRm = () => queryClient.invalidateQueries({ queryKey: ['raw-materials'] });

  // ── Add Stock ───────────────────────────────────────────────────
  // Same lists (and cache keys) the Sourcing Orders page uses for its dialog.
  const { data: allSourcers = [] } = useQuery({
    queryKey: ['sourcers-all'],
    queryFn: () => getSourcers(0, 100).then((r) => r.data.data.content),
    enabled: write,
  });

  const { data: allProducts = [] } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => getProducts(0, 100).then((r) => r.data.data.content),
    enabled: !!addStockModal,
  });

  const addStockMutation = useMutation({
    mutationFn: (form) => receiveSourcingOrderDirect(buildSourcingPayload(form)),
    onSuccess: () => {
      ['products', 'products-all', 'product-variants', 'product-cost-history', 'sourcing-orders', 'inventory']
        .forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      setAddStockModal(null);
      setAddStockError('');
    },
    onError: (err) => setAddStockError(err.response?.data?.message || 'Something went wrong.'),
  });

  const openAddStock = () => {
    const selfSourcer = allSourcers.find((s) => s.isDefault);
    setAddStockError('');
    setAddStockModal({ initialForm: { ...EMPTY_STOCK_FORM, sourcerId: selfSourcer ? String(selfSourcer.id) : '' } });
  };

  const rmSaveMutation = useMutation({
    mutationFn: (payload) => rmModal?.mode === 'edit'
      ? updateRawMaterial(rmModal.rm.id, payload)
      : createRawMaterial(payload),
    onSuccess: () => { invalidateRm(); setRmModal(null); setRmFormError(''); },
    onError: (err) => setRmFormError(err.response?.data?.message || 'Failed to save'),
  });

  const rmDeleteMutation = useMutation({
    mutationFn: (id) => deleteRawMaterial(id),
    onSuccess: () => { invalidateRm(); setRmDeleteTarget(null); },
    onError: (err) => alert(err.response?.data?.message || 'Cannot delete'),
  });

  // ── Products handlers ──────────────────────────────────────────
  // productFields is seeded by the effect above.
  const openCreateProduct = () => {
    setProductForm(EMPTY_PRODUCT_FORM);
    setProductFormError('');
    setImageList([]);
    setDeletedImageIds([]);
    setProductModal({ mode: 'create' });
  };

  const openEditProduct = (p) => {
    setProductForm({ title: p.title, description: p.description || '', formattedDesc: p.formattedDesc || '', price: p.price, mrp: p.mrp ?? p.price, brand: p.brand || '', categoryId: p.categoryId ?? '', subcategoryId: p.subcategoryId ?? '' });
    setProductFormError('');
    setImageList(buildImageList(p.images));
    setDeletedImageIds([]);
    setProductModal({ mode: 'edit', data: p });
  };

  const handleProductFilter = (key, val) => {
    setProductFilters((f) => ({
      ...f,
      [key]: val || undefined,
      // A subcategory filter only makes sense within its category.
      ...(key === 'categoryId' ? { subcategoryId: undefined } : {}),
    }));
    setProductPage(0);
  };
  const clearProductFilters = () => { setProductFilters({}); setProductPage(0); };

  const handleProductSubmit = (e) => {
    e.preventDefault();
    setProductFormError('');
    if (parseFloat(productForm.mrp) < parseFloat(productForm.price)) {
      setProductFormError(t('products.mrpTooLow'));
      return;
    }

    productSaveMutation.mutate({
      title: productForm.title,
      description: productForm.description || null,
      formattedDesc: productForm.formattedDesc.trim() ? productForm.formattedDesc : null,
      price: parseFloat(productForm.price),
      mrp: parseFloat(productForm.mrp),
      brand: productForm.brand.trim() || null,
      categoryId: productForm.categoryId ? parseInt(productForm.categoryId) : null,
      subcategoryId: productForm.subcategoryId ? parseInt(productForm.subcategoryId) : null,
      fields: cleanFields(productFields),
    });
  };

  // ── Image handlers ─────────────────────────────────────────────
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const available = MAX_IMAGES - imageList.filter(i => i.kind !== 'deleted').length;
    const toAdd = files.slice(0, available);
    const newItems = toAdd.map(file => ({
      kind: 'new',
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setImageList(prev => [...prev, ...newItems]);
    e.target.value = '';
  };

  const removeImage = (idx) => {
    const item = imageList[idx];
    if (item.kind === 'existing') {
      setDeletedImageIds(prev => [...prev, item.id]);
    } else {
      URL.revokeObjectURL(item.previewUrl);
    }
    setImageList(prev => prev.filter((_, i) => i !== idx));
  };

  const moveImage = (idx, direction) => {
    const newList = [...imageList];
    const target = idx + direction;
    if (target < 0 || target >= newList.length) return;
    [newList[idx], newList[target]] = [newList[target], newList[idx]];
    setImageList(newList);
  };

  // ── Raw Materials handlers ─────────────────────────────────────
  const openCreateRm = () => {
    setRmForm(EMPTY_RM_FORM);
    setRmFormError('');
    setRmModal({ mode: 'create' });
  };

  const openEditRm = (rm) => {
    setRmForm({
      name: rm.name,
      unit: rm.unit,
      categoryId: rm.categoryId ? String(rm.categoryId) : '',
      description: rm.description || '',
    });
    setRmFormError('');
    setRmModal({ mode: 'edit', rm });
  };

  const handleRmSubmit = (e) => {
    e.preventDefault();
    setRmFormError('');
    rmSaveMutation.mutate({
      name: rmForm.name,
      unit: rmForm.unit,
      categoryId: rmForm.categoryId ? parseInt(rmForm.categoryId) : null,
      description: rmForm.description || null,
    });
  };

  // ── Derived values ─────────────────────────────────────────────
  const products = productsData?.content ?? [];
  const productTotalPages = productsData?.totalPages ?? 0;
  const rmItems = rmData?.content ?? [];
  const rmTotalPages = rmData?.totalPages ?? 0;

  const outOfStock = products.filter(p => p.stockQuantity === 0).length;
  const lowStock = products.filter(p => p.stockQuantity > 0 && p.stockQuantity <= 5).length;

  const rmOutOfStock = rmItems.filter(r => parseFloat(r.stockQuantity) <= 0).length;
  const rmLowStock   = rmItems.filter(r => parseFloat(r.stockQuantity) > 0 && parseFloat(r.stockQuantity) < 5).length;

  const activeImageCount = imageList.length;
  const hasLargeImage = imageList.some(item => item.kind === 'new' && item.file.size > IMAGE_SIZE_WARNING_BYTES);

  return (
    <div className="p-6 max-w-5xl space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-gray-900">{t('inventory.title')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{t('inventory.subtitle')}</p>
      </div>

      {/* Summary cards */}
      {!productsLoading && products.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{t('inventory.totalSKUs')}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{productsData?.totalElements ?? 0}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4">
            <p className="text-xs text-yellow-600 font-medium uppercase tracking-wide">{t('inventory.lowStock')}</p>
            <p className="text-2xl font-bold text-yellow-700 mt-1">{lowStock}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4">
            <p className="text-xs text-red-500 font-medium uppercase tracking-wide">{t('inventory.outOfStock')}</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{outOfStock}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setSearchParams(key === 'products' ? {} : { tab: key })}
            className={`px-4 py-2 -mb-px text-sm border-b-2 transition-colors ${
              tab === key
                ? 'border-blue-600 text-blue-700 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Products section ── (hidden, not unmounted, so filters/page survive tab switches) */}
      <div hidden={tab !== 'products'}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-800">{t('inventory.tabProducts')}</h2>
          <div className="flex items-center gap-2">
            {write && (
              <button onClick={openGlobalStructureModal}
                className="flex items-center gap-2 border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
                <Sliders size={14} /> Common Structure
              </button>
            )}
            {write && (
              <button onClick={openCreateProduct}
                className="flex items-center gap-2 border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
                <Plus size={15} /> {t('products.new')}
              </button>
            )}
            {write && (
              <button onClick={openAddStock} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                <PackagePlus size={15} /> {t('inventory.addStock')}
              </button>
            )}
          </div>
        </div>
        <FilterBar
          fields={[
            { key: 'categoryId', type: 'select', label: t('common.category'), options:
              categories.map((c) => ({ value: String(c.id), label: c.name })) },
            ...(productFilters.categoryId ? [{ key: 'subcategoryId', type: 'select', label: t('common.subcategory'), options:
              subcategoriesOf(productFilters.categoryId).map((s) => ({ value: String(s.id), label: s.name })) }] : []),
            { key: 'active', type: 'select', label: 'Status', options: [
              { value: 'true', label: t('common.active') },
              { value: 'false', label: t('common.inactive') },
            ] },
          ]}
          values={productFilters} onChange={handleProductFilter} onClear={clearProductFilters}
        />
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {productsLoading ? (
              <div className="py-16 text-center text-sm text-gray-400">{t('common.loading')}</div>
            ) : products.length === 0 ? (
              <div className="py-16 text-center text-sm text-gray-400">{t('products.noProducts')}</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50 text-left">
                    <th className="px-4 py-3 font-medium text-gray-500 w-12"></th>
                    <th className="px-4 py-3 font-medium text-gray-500">{t('common.name')}</th>
                    <th className="px-4 py-3 font-medium text-gray-500">{t('common.category')}</th>
                    <th className="px-4 py-3 font-medium text-gray-500 text-right">{t('products.sellingPrice')}</th>
                    <th className="px-4 py-3 font-medium text-gray-500 text-right">{t('inventory.makingCost')}</th>
                    <th className="px-4 py-3 font-medium text-gray-500 text-right">{t('nav.inventory')}</th>
                    <th className="px-4 py-3 font-medium text-gray-500">{t('common.status')}</th>
                    <th className="px-4 py-3 w-24 font-medium text-gray-500">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {products.map((p) => {
                    const thumbnail = p.images?.[0]?.url;
                    return (
                      <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          {thumbnail ? (
                            <img src={thumbnail} alt={p.title}
                              className="w-9 h-9 rounded-lg object-cover border border-gray-200 shrink-0" />
                          ) : (
                            <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                              <ImageIcon size={14} className="text-gray-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button type="button" onClick={() => setVariantDetailModal({ product: p })}
                            className="font-medium text-gray-800 hover:text-blue-600 hover:underline transition-colors text-left">
                            {p.title}
                          </button>
                          {p.brand && <p className="text-xs text-gray-400">{p.brand}</p>}
                          {p.description && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{p.description}</p>}
                          {inStockVariants(p).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {inStockVariants(p).slice(0, MAX_VARIANT_CHIPS).map(v => (
                                <span key={v.id} className={`text-xs px-1.5 py-0.5 rounded ${stockBadgeClass(v.stockQuantity)}`}
                                  title={formatCombo(v.combo)}>
                                  {Object.values(v.combo).join(' · ')} <span className="font-semibold">{v.stockQuantity}</span>
                                </span>
                              ))}
                              {inStockVariants(p).length > MAX_VARIANT_CHIPS && (
                                <button type="button" onClick={() => setVariantDetailModal({ product: p })}
                                  className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
                                  {t('inventory.moreVariants', { count: inStockVariants(p).length - MAX_VARIANT_CHIPS })}
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {p.categoryName
                            ? <>{p.categoryName}{p.subcategoryName && <span className="text-gray-400"> › {p.subcategoryName}</span>}</>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-800 text-right font-medium whitespace-nowrap">
                          {Number(p.mrp) > Number(p.price) && (
                            <span className="text-xs text-gray-400 line-through font-normal mr-1.5" title={t('products.mrp')}>৳{formatPrice(p.mrp)}</span>
                          )}
                          ৳{formatPrice(p.price)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {p.lastMakingCost != null ? (
                            <div className="inline-flex items-center gap-1.5 justify-end">
                              <span className="text-gray-700 font-medium">৳{formatPrice(p.lastMakingCost)}</span>
                              {p.costRecordCount > 1 && (
                                <button
                                  onClick={() => setCostHistoryModal({ product: p })}
                                  className="text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 px-1.5 py-0.5 rounded transition-colors"
                                >
                                  +{p.costRecordCount - 1}
                                </button>
                              )}
                            </div>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${stockBadgeClass(p.stockQuantity)}`}>
                            {p.stockQuantity}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge active={p.active} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            {/* BOM = raw-material recipe, so it's hidden with raw materials in Simple view */}
                            {!simpleView && (
                              <Tip label={t('products.manageBom')}>
                                <button onClick={() => { setBomItems([]); setBomError(''); setBomModal({ product: p }); }}
                                  className="p-1.5 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors">
                                  <FlaskConical size={14} />
                                </button>
                              </Tip>
                            )}
                            {write && <>
                              <Tip label={p.active ? 'Deactivate' : 'Activate'}>
                                <button onClick={() => productToggleMutation.mutate(p.id)}
                                  className={`p-1.5 rounded-lg transition-colors ${
                                    p.active
                                      ? 'bg-green-50 text-green-600 hover:bg-green-100'
                                      : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                  }`}>
                                  {p.active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                                </button>
                              </Tip>
                              <Tip label={t('common.edit')}>
                                <button onClick={() => openEditProduct(p)} className="p-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"><Pencil size={14} /></button>
                              </Tip>
                              <Tip label={t('common.delete')}>
                                <button onClick={() => setProductDeleteTarget(p)} className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"><Trash2 size={14} /></button>
                              </Tip>
                            </>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        <Pagination page={productPage} totalPages={productTotalPages} onPageChange={setProductPage} />
      </div>

      {/* ── Raw Materials section (not in Simple view) ── */}
      {!simpleView && (
        <div hidden={tab !== 'raw-materials'}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-semibold text-gray-800">{t('inventory.tabRawMaterials')}</h2>
              {!rmLoading && rmItems.length > 0 && (
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-gray-500">{rmData?.totalElements ?? rmItems.length} {t('common.total')}</span>
                  {rmLowStock > 0 && <span className="text-xs text-yellow-600 font-medium">{rmLowStock} {t('inventory.lowStock')}</span>}
                  {rmOutOfStock > 0 && <span className="text-xs text-red-500 font-medium">{rmOutOfStock} {t('inventory.outOfStock')}</span>}
                </div>
              )}
            </div>
            {write && (
              <button onClick={openCreateRm} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                <Plus size={15} /> {t('rawMaterials.newMaterial')}
              </button>
            )}
          </div>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {rmLoading ? (
                <div className="py-16 text-center text-sm text-gray-400">{t('common.loading')}</div>
              ) : rmItems.length === 0 ? (
                <div className="py-16 text-center text-sm text-gray-400">{t('rawMaterials.noMaterials')}</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50 text-left">
                      <th className="px-4 py-3 font-medium text-gray-500">{t('common.name')}</th>
                      <th className="px-4 py-3 font-medium text-gray-500">{t('common.category')}</th>
                      <th className="px-4 py-3 font-medium text-gray-500">{t('common.unit')}</th>
                      <th className="px-4 py-3 font-medium text-gray-500 text-right">{t('nav.inventory')}</th>
                      <th className="px-4 py-3 font-medium text-gray-500">{t('common.description')}</th>
                      <th className="px-4 py-3 w-24 font-medium text-gray-500">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rmItems.map((rm) => (
                      <tr key={rm.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900">{rm.name}</td>
                        <td className="px-4 py-3 text-gray-500">{rm.categoryName ?? <span className="text-gray-300">—</span>}</td>
                        <td className="px-4 py-3 text-gray-500">{rm.unit}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                            parseFloat(rm.stockQuantity) <= 0 ? 'bg-red-50 text-red-600'
                            : parseFloat(rm.stockQuantity) < 5  ? 'bg-yellow-50 text-yellow-700'
                            : 'bg-green-50 text-green-700'
                          }`}>
                            {parseFloat(rm.stockQuantity).toLocaleString('en-US', { maximumFractionDigits: 3 })} {rm.unit}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 max-w-xs truncate">{rm.description ?? <span className="text-gray-300">—</span>}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            <Tip label={t('inventory.viewBatches')}>
                              <button onClick={() => setBatchesModal({ rm })} className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"><Eye size={14} /></button>
                            </Tip>
                            {write && <>
                              <Tip label={t('common.edit')}>
                                <button onClick={() => openEditRm(rm)} className="p-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"><Pencil size={14} /></button>
                              </Tip>
                              <Tip label={t('common.delete')}>
                                <button onClick={() => setRmDeleteTarget(rm)} className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"><Trash2 size={14} /></button>
                              </Tip>
                            </>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          <Pagination page={rmPage} totalPages={rmTotalPages} onPageChange={setRmPage} />
        </div>
      )}

      {/* ── Sourcing history: every stock-in; received product entries can be corrected ── */}
      {tab === 'history' && (
        <SourcingOrders embedded productOnly={simpleView} hideSourcer={simpleView} />
      )}

      {/* ── Product Create / Edit Modal ── */}
      {productModal && (
        <Modal
          title={productModal.mode === 'create' ? t('products.new') : `${t('common.edit')} ${t('products.title')}`}
          onClose={() => setProductModal(null)}
          wide
        >
          <form onSubmit={handleProductSubmit} className="space-y-5">
            {/* Basic fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} <span className="text-red-500">*</span></label>
                <input type="text" value={productForm.title} onChange={e => setProductForm({ ...productForm, title: e.target.value })}
                  required autoFocus
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('products.titlePlaceholder')} />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.brand')}</label>
                <input type="text" value={productForm.brand} onChange={e => setProductForm({ ...productForm, brand: e.target.value })}
                  maxLength={100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('products.brandPlaceholder')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.category')} <span className="text-red-500">*</span></label>
                <select
                  value={productForm.categoryId}
                  // Changing the category clears the subcategory: it must belong to the new one.
                  onChange={e => setProductForm(prev => ({ ...prev, categoryId: e.target.value, subcategoryId: '' }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                  <option value="">{t('products.selectCategory')}</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.subcategory')} <span className="text-red-500">*</span></label>
                <select
                  value={productForm.subcategoryId}
                  onChange={e => setProductForm(prev => ({ ...prev, subcategoryId: e.target.value }))}
                  required
                  disabled={!productForm.categoryId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-50 disabled:text-gray-400">
                  <option value="">{t('products.selectSubcategory')}</option>
                  {formSubcategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {productForm.categoryId && formSubcategories.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">{t('products.noSubcategoriesHint')}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.sellingPrice')} <span className="text-red-500">*</span></label>
                <input type="number" value={productForm.price} onChange={e => setProductForm({ ...productForm, price: e.target.value })}
                  required min="0" step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.mrp')} <span className="text-red-500">*</span></label>
                <input type="number" value={productForm.mrp} onChange={e => setProductForm({ ...productForm, mrp: e.target.value })}
                  required min="0" step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00" />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
              <textarea value={productForm.description} onChange={e => setProductForm({ ...productForm, description: e.target.value })} rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder={t('products.descriptionPlaceholder')} />
            </div>

            {/* Detailed description in Markdown — returned as-is by the external API for storefronts to render */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.formattedDesc')}</label>
              <MarkdownEditor value={productForm.formattedDesc} onChange={v => setProductForm(prev => ({ ...prev, formattedDesc: v }))}
                maxLength={20000} placeholder={t('products.formattedDescPlaceholder')} />
            </div>

            {/* This product's own field structure — defined inline, on top of the Common Structure.
                Field values aren't entered here: they're supplied later, per combination, when
                stock comes in via a Sourcing Order, and selected by the customer at order time. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Product Fields</label>
              <FieldDefinitionEditor fields={productFields} onChange={setProductFields} write={write} />
            </div>

            {/* Images */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Images <span className="text-gray-400 font-normal">({activeImageCount}/{MAX_IMAGES}) — first image is thumbnail</span>
                </label>
                {write && activeImageCount < MAX_IMAGES && (
                  <button type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                    <Plus size={12} /> Add image
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageSelect}
              />

              {hasLargeImage && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
                  Some images are larger than 500 KB. Consider using smaller images for faster loading.
                </p>
              )}

              {imageList.length === 0 ? (
                <button type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-gray-200 rounded-xl py-8 flex flex-col items-center gap-2 text-gray-400 hover:border-blue-300 hover:text-blue-400 transition-colors">
                  <ImageIcon size={24} />
                  <span className="text-sm">Click to add images</span>
                </button>
              ) : (
                <div className="flex gap-3 flex-wrap">
                  {imageList.map((item, idx) => {
                    const src = item.kind === 'existing' ? item.url : item.previewUrl;
                    const isLarge = item.kind === 'new' && item.file.size > IMAGE_SIZE_WARNING_BYTES;
                    return (
                      <div key={idx} className="relative group">
                        <img src={src} alt={`img-${idx}`}
                          className={`w-24 h-24 object-cover rounded-xl border-2 ${idx === 0 ? 'border-blue-400' : 'border-gray-200'}`} />
                        {idx === 0 && (
                          <span className="absolute top-1 left-1 text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded font-medium">Thumb</span>
                        )}
                        {isLarge && (
                          <span className="absolute top-1 right-1 text-xs bg-amber-500 text-white px-1 py-0.5 rounded">!</span>
                        )}
                        {write && (
                          <button type="button" onClick={() => removeImage(idx)}
                            className="absolute -top-2 -right-2 hidden group-hover:flex w-5 h-5 bg-red-500 text-white rounded-full items-center justify-center">
                            <X size={10} />
                          </button>
                        )}
                        <div className="absolute bottom-1 left-0 right-0 hidden group-hover:flex items-center justify-center gap-1">
                          <button type="button" onClick={() => moveImage(idx, -1)} disabled={idx === 0}
                            className="w-5 h-5 bg-black/60 text-white rounded flex items-center justify-center disabled:opacity-30">
                            <ChevronLeft size={10} />
                          </button>
                          <button type="button" onClick={() => moveImage(idx, 1)} disabled={idx === imageList.length - 1}
                            className="w-5 h-5 bg-black/60 text-white rounded flex items-center justify-center disabled:opacity-30">
                            <ChevronRight size={10} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {activeImageCount < MAX_IMAGES && (
                    <button type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-24 h-24 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-gray-400 hover:border-blue-300 hover:text-blue-400 transition-colors">
                      <Plus size={16} />
                    </button>
                  )}
                </div>
              )}
            </div>

            {productFormError && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{productFormError}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setProductModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
              <button type="submit" disabled={productSaveMutation.isPending}
                className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                {productSaveMutation.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Product Delete Modal ── */}
      {productDeleteTarget && (
        <Modal title={t('products.deleteProduct')} onClose={() => setProductDeleteTarget(null)}>
          <p className="text-sm text-gray-600">
            {t('products.confirmDelete')}{' '}
            <span className="font-medium text-gray-900">{productDeleteTarget.title}</span>?{' '}
            {t('common.cannotBeUndone')}
          </p>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setProductDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
            <button onClick={() => productDeleteMutation.mutate(productDeleteTarget.id)} disabled={productDeleteMutation.isPending}
              className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors">
              {productDeleteMutation.isPending ? t('common.deleting') : t('common.delete')}
            </button>
          </div>
        </Modal>
      )}

      {/* Common (global/vendor-wide) Structure Modal */}
      {globalStructureModal && (
        <Modal title="Common Product Fields" onClose={() => setGlobalStructureModal(false)} wide>
          <p className="text-sm text-gray-500 mb-4">
            These fields appear on every product, combined with each product's own fields
            (a product field with the same name overrides the common one).
          </p>
          <FieldDefinitionEditor fields={globalStructureFields} onChange={setGlobalStructureFields} write={write} />
          {globalStructureError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3">
              {globalStructureError}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={() => setGlobalStructureModal(false)}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              {t('common.cancel')}
            </button>
            {write && (
              <button type="button" onClick={handleGlobalStructureSave} disabled={globalStructureSaveMutation.isPending}
                className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                {globalStructureSaveMutation.isPending ? t('common.saving') : t('common.save')}
              </button>
            )}
          </div>
        </Modal>
      )}

      {/* BOM Modal */}
      {bomModal && (
        <Modal title={t('products.bomModalTitle', { title: bomModal.product.title })} onClose={() => setBomModal(null)} wide>
          <div className="space-y-4">
            {bomData?.estimatedCostPerUnit != null && (
              <div className="flex items-center gap-2 text-sm bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <FlaskConical size={14} className="text-blue-600 shrink-0" />
                <span className="text-blue-700">{t('products.estimatedCost')} <strong>৳{parseFloat(bomData.estimatedCostPerUnit).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></span>
              </div>
            )}
            <div className="space-y-2">
              {bomItems.map((item, idx) => {
                const rm = rawMaterialsList.find(r => String(r.id) === String(item.rawMaterialId));
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <select value={item.rawMaterialId}
                      onChange={e => setBomItems(bomItems.map((it, i) => i === idx ? { ...it, rawMaterialId: e.target.value } : it))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value="">{t('products.selectIngredient')}</option>
                      {rawMaterialsList.map(r => <option key={r.id} value={r.id}>{r.name} ({r.unit})</option>)}
                    </select>
                    <input type="number" min="0.001" step="any" placeholder="Qty" value={item.quantity}
                      onChange={e => setBomItems(bomItems.map((it, i) => i === idx ? { ...it, quantity: e.target.value } : it))}
                      className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    <span className="text-xs text-gray-400 w-10">{rm?.unit ?? ''}</span>
                    {write && (
                      <button onClick={() => setBomItems(bomItems.filter((_, i) => i !== idx))}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
              {bomItems.length === 0 && <p className="text-sm text-gray-400 py-2">{t('products.noIngredients')}</p>}
            </div>
            {write && (
              <button onClick={() => setBomItems([...bomItems, { rawMaterialId: '', quantity: '' }])}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
                <Plus size={14} /> {t('products.addIngredient')}
              </button>
            )}
            {bomError && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{bomError}</p>}
            {write && (
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setBomModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
                <button
                  onClick={() => {
                    setBomError('');
                    const items = bomItems.filter(i => i.rawMaterialId && i.quantity);
                    bomMutation.mutate(items.map(i => ({ rawMaterialId: parseInt(i.rawMaterialId), quantity: parseFloat(i.quantity) })));
                  }}
                  disabled={bomMutation.isPending}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                  {bomMutation.isPending ? t('common.saving') : t('products.saveBom')}
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Cost History Modal */}
      {costHistoryModal && (
        <Modal title={`${costHistoryModal.product.title} — ${t('inventory.costHistory')}`} onClose={() => setCostHistoryModal(null)} wide>
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-left">
                  <th className="px-3 py-2 font-medium text-gray-500">{t('common.type')}</th>
                  <th className="px-3 py-2 font-medium text-gray-500">{t('common.date')}</th>
                  <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('inventory.makingCost')}</th>
                  <th className="px-3 py-2 font-medium text-gray-500">{t('common.notes')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {costHistoryLoading ? (
                  <tr><td colSpan={4} className="px-3 py-10 text-center text-sm text-gray-400">{t('common.loading')}</td></tr>
                ) : !costHistory?.length ? (
                  <tr><td colSpan={4} className="px-3 py-10 text-center text-sm text-gray-400">—</td></tr>
                ) : costHistory.map((entry, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      {entry.source === 'MANUFACTURE'
                        ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">{t('inventory.costSourceManufacture')}</span>
                        : <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{t('inventory.costSourceSourcing')}</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-600 text-xs whitespace-nowrap">
                      {new Date(entry.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-gray-800">৳{formatPrice(entry.costPerUnit)}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{entry.label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* Variant Stock Detail Modal */}
      {variantDetailModal && (
        <Modal title={`${variantDetailModal.product.title} — Stock by Variant`} onClose={() => setVariantDetailModal(null)}>
          {variantDetailLoading ? (
            <div className="py-10 text-center text-sm text-gray-400">{t('common.loading')}</div>
          ) : !variantDetailData?.length ? (
            <div className="py-6 text-center text-sm text-gray-500">
              This product has no variant fields — stock is tracked as a single total:{' '}
              <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${stockBadgeClass(variantDetailModal.product.stockQuantity)}`}>
                {variantDetailModal.product.stockQuantity}
              </span>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50 text-left">
                    <th className="px-3 py-2 font-medium text-gray-500">Combination</th>
                    <th className="px-3 py-2 font-medium text-gray-500 text-right">Stock</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {variantDetailData.map(v => (
                    <tr key={v.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-800">{formatCombo(v.combo)}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${stockBadgeClass(v.stockQuantity)}`}>
                          {v.stockQuantity}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-200 bg-gray-50">
                    <td className="px-3 py-2 text-sm font-semibold text-gray-700">Total</td>
                    <td className="px-3 py-2 text-right font-bold text-gray-900">
                      {variantDetailData.reduce((sum, v) => sum + v.stockQuantity, 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Modal>
      )}

      {/* Raw Material Create / Edit Modal */}
      {rmModal && (
        <Modal title={rmModal.mode === 'edit' ? t('rawMaterials.editMaterial') : t('rawMaterials.newMaterial')} onClose={() => setRmModal(null)}>
          <form onSubmit={handleRmSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} <span className="text-red-500">*</span></label>
              <input type="text" value={rmForm.name} onChange={e => setRmForm({ ...rmForm, name: e.target.value })} required autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g. Honey" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.unit')} <span className="text-red-500">*</span></label>
              <select value={rmForm.unit} onChange={e => setRmForm({ ...rmForm, unit: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.category')}</label>
              <select value={rmForm.categoryId} onChange={e => setRmForm({ ...rmForm, categoryId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                <option value="">{t('rawMaterials.noCategory')}</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
              <textarea value={rmForm.description} onChange={e => setRmForm({ ...rmForm, description: e.target.value })} rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder={t('rawMaterials.descriptionPlaceholder')} />
            </div>
            {rmFormError && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{rmFormError}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setRmModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
              <button type="submit" disabled={rmSaveMutation.isPending} className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                {rmSaveMutation.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Raw Material Batches Modal */}
      {batchesModal && (
        <Modal title={`${batchesModal.rm.name} — ${t('inventory.remainingBatches')}`} onClose={() => setBatchesModal(null)} wide>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {t('inventory.totalRemaining')}:{' '}
                <span className="font-semibold text-gray-800">
                  {parseFloat(batchesModal.rm.stockQuantity).toLocaleString('en-US', { maximumFractionDigits: 3 })} {batchesModal.rm.unit}
                </span>
              </p>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50 text-left">
                    <th className="px-3 py-2 font-medium text-gray-500">{t('common.date')}</th>
                    <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('common.qty')}</th>
                    <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('rawMaterials.remaining')}</th>
                    <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('rawMaterials.totalPaid')}</th>
                    <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('rawMaterials.derivedRate')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {batchesLoading ? (
                    <tr><td colSpan={5} className="px-3 py-10 text-center text-sm text-gray-400">{t('common.loading')}</td></tr>
                  ) : !batchesData?.content?.length ? (
                    <tr><td colSpan={5} className="px-3 py-10 text-center text-sm text-gray-400">{t('rawMaterials.noPurchases')}</td></tr>
                  ) : batchesData.content.map(b => {
                    const remaining = parseFloat(b.remainingQuantity);
                    const qty = parseFloat(b.quantity);
                    const fullyConsumed = remaining <= 0;
                    const rate = qty > 0 ? (parseFloat(b.totalCost) / qty) : 0;
                    return (
                      <tr key={b.id} className={fullyConsumed ? 'bg-gray-50 opacity-50' : 'hover:bg-gray-50'}>
                        <td className="px-3 py-2 text-gray-700">{b.purchasedAt}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{qty.toLocaleString('en-US', { maximumFractionDigits: 3 })} {batchesModal.rm.unit}</td>
                        <td className="px-3 py-2 text-right">
                          <span className={remaining < qty ? 'text-amber-600 font-medium' : 'text-green-700 font-medium'}>
                            {remaining.toLocaleString('en-US', { maximumFractionDigits: 3 })} {batchesModal.rm.unit}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700">৳{parseFloat(b.totalCost).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2 text-right text-gray-500 text-xs">
                          ৳{rate.toLocaleString('en-US', { minimumFractionDigits: 2 })}/{batchesModal.rm.unit}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Modal>
      )}

      {/* Raw Material Delete Modal */}
      {rmDeleteTarget && (
        <Modal title={t('rawMaterials.deleteMaterial')} onClose={() => setRmDeleteTarget(null)}>
          <p className="text-sm text-gray-600 mb-4"><strong>{rmDeleteTarget.name}</strong>? {t('common.cannotBeUndone')}</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setRmDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
            <button onClick={() => rmDeleteMutation.mutate(rmDeleteTarget.id)} disabled={rmDeleteMutation.isPending}
              className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors">
              {rmDeleteMutation.isPending ? t('common.deleting') : t('common.delete')}
            </button>
          </div>
        </Modal>
      )}

      {addStockModal && (
        <SourcingOrderForm
          mode="create"
          lockType
          hideSourcer={simpleView}
          title={t('inventory.addStock')}
          submitLabel={t('inventory.receiveStock')}
          initialForm={addStockModal.initialForm}
          sourcers={allSourcers}
          products={allProducts}
          rawMaterials={[]}
          globalFields={globalStructureData?.fields ?? []}
          onSubmit={(form) => addStockMutation.mutate(form)}
          onClose={() => { setAddStockModal(null); setAddStockError(''); }}
          isPending={addStockMutation.isPending}
          error={addStockError}
        />
      )}
    </div>
  );
}
